import { motion } from "framer-motion";
import { ArrowRight, Check, X, LineChart, MessagesSquare, FileWarning, Sparkles, Lock, LogIn } from "lucide-react";

/**
 * ClientOps feature section — positions the in-house operating system as an
 * exclusive perk of being an NZ Taxation Limited client. Sits after Pricing so
 * it acts as the closing "wait, that's included?" moment.
 */
export default function ClientOps() {
  return (
    <section
      id="clientops"
      className="relative bg-[#0A192F] text-white overflow-hidden scroll-mt-16 md:scroll-mt-20"
      data-testid="clientops-section"
    >
      {/* Ambient gold glow to give the navy some depth */}
      <div
        aria-hidden
        className="absolute top-0 -right-40 w-[36rem] h-[36rem] bg-[#C9A349]/10 blur-3xl pointer-events-none"
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -left-40 w-[32rem] h-[32rem] bg-[#C9A349]/5 blur-3xl pointer-events-none"
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-24 md:pb-32">
        {/* ---------------- Hero row: pitch + phone side-by-side ---------------- */}
        {/* Consolidated so #clientops lands on the phone mockup with all the
            headline copy alongside it — no scrolling needed. */}
        <div
          className="grid md:grid-cols-12 gap-10 md:gap-14 items-center"
          data-testid="clientops-hero"
        >
          {/* Left — pitch, headline, three bullets, CTA */}
          <div className="md:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 border border-[#C9A349]/40 bg-[#C9A349]/10 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] font-bold text-[#C9A349]"
            >
              <Lock className="w-3 h-3" />
              ClientOps · Exclusive to NZ Taxation clients
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-6 font-heading font-black text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.02]"
              data-testid="clientops-headline"
            >
              A financial operating system.
              <br />
              <span className="text-[#C9A349]">Not just a compliance return.</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: 0.12 }}
              className="mt-6 text-base md:text-lg text-slate-300 max-w-2xl leading-relaxed"
            >
              Most accountants file your tax once a year.{" "}
              <strong className="text-white">ClientOps</strong> reviews your cash flow,
              business health score, and missing documents{" "}
              <strong className="text-white">every day</strong> — built in-house, available
              only to businesses we work with.
            </motion.p>

            {/* Three phrase bullets */}
            <ul className="mt-8 space-y-4 max-w-xl">
              <PhraseBullet
                title="Today's 3 things that matter"
                body="What to fix, what to watch, what to press on. Curated by your accountant, not an algorithm."
              />
              <PhraseBullet
                title="Live cash-flow score"
                body="A single number that tells you whether the month is on track — updated as your bank feeds land."
              />
              <PhraseBullet
                title="Daybook Coach"
                body="A real accountant messaging you, plain-English, in the app. Not a chatbot — your accountant."
              />
            </ul>

            {/* CTA row */}
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <a
                href="#pricing"
                className="inline-flex items-center gap-2 bg-[#C9A349] text-[#0A192F] px-5 py-3.5 text-xs md:text-sm font-heading font-bold uppercase tracking-[0.14em] hover:bg-white transition-colors"
                data-testid="clientops-hero-cta"
              >
                See monthly plans <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="https://www.clientops.nz/?utm_source=nztaxation-site"
                target="_blank"
                rel="noreferrer"
                title="ClientOps — Client data flow for accounting firms"
                className="group inline-flex items-center gap-2 border border-[#C9A349]/40 bg-[#C9A349]/5 hover:bg-[#C9A349] hover:text-[#0A192F] text-[#C9A349] px-5 py-3.5 text-xs md:text-sm font-heading font-bold uppercase tracking-[0.14em] transition-colors"
                data-testid="clientops-signin-top"
              >
                <LogIn className="w-4 h-4" />
                Existing client · Sign in
                <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          {/* Right — phone mockup */}
          <div className="md:col-span-5 flex justify-center md:justify-end">
            <PhoneFrame src="/clientops-dashboard.png" />
          </div>
        </div>

        {/* ---------------- Comparison table ---------------- */}
        <div className="mt-24 md:mt-32">
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-400 mb-4">
            The old way vs the ClientOps way
          </div>
          <ComparisonTable />
        </div>

        {/* ---------------- Three-feature grid ---------------- */}
        <div className="mt-20 md:mt-24 grid md:grid-cols-3 gap-4 md:gap-5">
          <FeatureCard
            icon={LineChart}
            title="Daily cash flow visibility"
            body="A live dashboard showing your cash position, business health score, and 30-day outlook. No annual surprises — you see the trend the moment it changes."
          />
          <FeatureCard
            icon={MessagesSquare}
            title="A real accountant, in the app"
            body="Ask a question, get an answer from your accountant — not a chatbot. Advice lives inside the app, timestamped, searchable, and tied to the transaction it relates to."
          />
          <FeatureCard
            icon={FileWarning}
            title="Missing docs, flagged early"
            body="A guided checklist tailored to your entity type. Forward a receipt from your email and it lands in the right pile automatically. Nothing falls through the cracks."
          />
        </div>

        {/* ---------------- CTA ---------------- */}
        <div className="mt-20 md:mt-24 grid md:grid-cols-12 gap-8 items-center border-t border-white/10 pt-16">
          <div className="md:col-span-8">
            <h3 className="font-heading font-black text-3xl md:text-4xl tracking-tight leading-tight">
              ClientOps is built in-house.
              <br />
              <span className="text-[#C9A349]">And it&apos;s only for our clients.</span>
            </h3>
            <p className="mt-4 text-slate-300 max-w-2xl leading-relaxed">
              You don&apos;t buy ClientOps separately — it comes bundled with every monthly subscription.
              Pick a plan and we&apos;ll set you up in your first week.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-col md:items-end gap-3">
            <a
              href="#pricing"
              className="inline-flex items-center gap-2 bg-[#C9A349] text-[#0A192F] px-6 py-4 font-heading font-bold uppercase tracking-[0.12em] text-sm hover:bg-white transition-colors"
              data-testid="clientops-cta"
            >
              Become a NZ Taxation client <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="https://www.clientops.nz/?utm_source=nztaxation-site"
              target="_blank"
              rel="noreferrer"
              title="ClientOps — Client data flow for accounting firms"
              className="group inline-flex items-center gap-2 text-sm text-slate-300 hover:text-[#C9A349] transition-colors"
              data-testid="clientops-signin"
            >
              <LogIn className="w-4 h-4" />
              <span>
                Already a client?{" "}
                <span className="font-heading font-bold underline underline-offset-4 decoration-[#C9A349]/50 group-hover:decoration-[#C9A349]">
                  Sign in to ClientOps
                </span>
              </span>
              <ArrowRight className="w-3.5 h-3.5 -translate-x-0.5 group-hover:translate-x-0 transition-transform" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Building blocks ---------- */

function ComparisonTable() {
  const columns = [
    { key: "row", label: "" },
    { key: "compliance", label: "Compliance accountant", tone: "muted" },
    { key: "chartered", label: "Chartered firm", tone: "muted" },
    { key: "clientops", label: "ClientOps", tone: "gold" },
  ];
  const rows = [
    ["Reporting frequency", "Once a year", "Quarterly", "Daily"],
    ["Cash flow visibility", "None", "On request", "Live dashboard"],
    ["Missing docs & risk", "Found at year-end", "Found at review", "Flagged before it costs you"],
    ["Advice", "Filed, not explained", "Annual meeting", "Coach inside the app"],
    ["Getting docs to us", "Email & spreadsheets", "Client portal", "Guided checklist + forward-to-upload email"],
  ];

  return (
    <div
      className="relative border border-white/10 bg-black/20 backdrop-blur overflow-x-auto"
      data-testid="clientops-comparison-table"
    >
      <table className="w-full min-w-[720px] text-sm md:text-[15px]">
        <thead>
          <tr className="border-b border-white/10">
            {columns.map((c) => (
              <th
                key={c.key}
                className={`text-left px-5 md:px-7 py-5 md:py-6 font-heading font-bold ${
                  c.tone === "gold"
                    ? "bg-[#C9A349]/12 text-[#C9A349] uppercase tracking-[0.18em] text-xs md:text-sm"
                    : c.tone === "muted"
                      ? "text-slate-400 uppercase tracking-[0.18em] text-xs"
                      : ""
                }`}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, a, b, cVal], i) => (
            <tr
              key={label}
              className={`border-b border-white/5 ${i === rows.length - 1 ? "border-b-0" : ""}`}
            >
              <td className="px-5 md:px-7 py-5 text-slate-300 font-medium">{label}</td>
              <MutedCell>
                <X className="w-4 h-4 text-red-400/70 flex-shrink-0" />
                <span>{a}</span>
              </MutedCell>
              <MutedCell>
                <X className="w-4 h-4 text-slate-500 flex-shrink-0" />
                <span>{b}</span>
              </MutedCell>
              <td className="px-5 md:px-7 py-5 bg-[#C9A349]/8 border-l border-[#C9A349]/25">
                <div className="flex items-center gap-2.5">
                  <Check className="w-4 h-4 text-[#C9A349] flex-shrink-0" />
                  <span className="text-white font-medium">{cVal}</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MutedCell({ children }) {
  return (
    <td className="px-5 md:px-7 py-5 text-slate-400">
      <div className="flex items-center gap-2.5">{children}</div>
    </td>
  );
}

function FeatureCard({ icon: Icon, title, body }) {
  return (
    <div className="group relative border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-colors p-6 md:p-7">
      <div className="w-11 h-11 flex items-center justify-center bg-[#C9A349] text-[#0A192F]">
        <Icon className="w-5 h-5" />
      </div>
      <h4 className="mt-5 font-heading font-black text-xl tracking-tight leading-tight">
        {title}
      </h4>
      <p className="mt-3 text-sm md:text-[15px] text-slate-400 leading-relaxed">{body}</p>
      <Sparkles className="absolute top-6 right-6 w-3.5 h-3.5 text-[#C9A349]/40" />
    </div>
  );
}

function PhraseBullet({ title, body }) {
  return (
    <li className="flex items-start gap-4">
      <span className="mt-2 w-2 h-2 bg-[#C9A349] flex-shrink-0 rotate-45" aria-hidden />
      <div>
        <div className="font-heading font-black text-white text-base md:text-lg tracking-tight">
          {title}
        </div>
        <p className="mt-1 text-sm md:text-[15px] text-slate-400 leading-relaxed">{body}</p>
      </div>
    </li>
  );
}

function PhoneFrame({ src }) {
  // Realistic iPhone-style bezel: 12px outer bezel, rounded corners, dynamic
  // island up top, subtle gold hairline. Keeps the screenshot on-message.
  return (
    <div className="relative w-[260px] sm:w-[300px] md:w-[320px] lg:w-[340px] shrink-0">
      {/* Soft shadow underneath */}
      <div
        aria-hidden
        className="absolute inset-x-4 -bottom-4 h-8 bg-black/60 blur-2xl rounded-full pointer-events-none"
      />
      {/* Outer device */}
      <div className="relative bg-black rounded-[42px] p-2.5 border border-white/10 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.85)]">
        {/* Gold hairline inside the bezel */}
        <div
          aria-hidden
          className="absolute inset-2 rounded-[36px] border border-[#C9A349]/40 pointer-events-none"
        />
        {/* Screen */}
        <div className="relative rounded-[34px] overflow-hidden bg-white">
          {/* Dynamic-island style top bar */}
          <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-full z-10" />
          <img
            src={src}
            alt="ClientOps home dashboard on a phone — Today's 3 Things That Matter: fix cash balance, upload missing documents, protect profit margin. Cash flow overview, business health score of 67, and Daybook Coach message from your accountant."
            width="607"
            height="976"
            className="block w-full h-auto"
            loading="lazy"
          />
        </div>
      </div>
      {/* Caption below phone */}
      <div className="mt-4 text-center">
        <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-bold text-[#C9A349]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#C9A349] animate-pulse" />
          Live product · client view
        </span>
      </div>
    </div>
  );
}


